import { useEffect, useState } from "react";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useSettingsCache } from "@/shared/cache";
import { Switch } from "@/v2/components/ui/switch";
import { SettingRow } from "@/v2/components/settings/SettingRow";
import { SectionHeading } from "@/v2/components/settings/SectionHeading";
import { NumberStepper } from "@/v2/components/settings/NumberStepper";
import { Textarea } from "@/v2/components/ui/textarea";
import { Button } from "@/v2/components/ui/button";
import type { Settings } from "@/shared/cache/slices/settings.types";

const PROMPT_FIELD_MAX_CHARS = 2000;

// Each per-timekeeper prompt block shares the same UX (textarea + counter +
// dirty-tracked Save/Cancel + over-limit guard). The data layer wires it to a
// specific Settings key so each block stays independently dirty/savable.
type PromptKey =
	| "p_narrative_style_prompt"
	| "p_group_behavior_prompt"
	| "p_merge_prompt"
	| "p_collapse_prompt";

interface PromptBlockProps {
	title: string;
	description: string;
	placeholder: string;
	settingKey: PromptKey;
	settings: Settings;
	updateSettings: (patch: Partial<Settings>) => unknown;
}

function PromptBlock({
	title,
	description,
	placeholder,
	settingKey,
	settings,
	updateSettings,
}: PromptBlockProps) {
	const current = settings[settingKey];
	const [draft, setDraft] = useState(current);
	useEffect(() => {
		setDraft(current);
	}, [current]);
	const isDirty = draft !== current;
	const overLimit = draft.length > PROMPT_FIELD_MAX_CHARS;

	return (
		<div className="flex flex-col gap-3 px-2 py-4 border-b border-border/40">
			<div>
				<p className="text-sm font-medium">{title}</p>
				<p className="text-sm text-muted-foreground">{description}</p>
			</div>
			<Textarea
				value={draft}
				onChange={(e) =>
					setDraft(e.target.value.slice(0, PROMPT_FIELD_MAX_CHARS))
				}
				maxLength={PROMPT_FIELD_MAX_CHARS}
				placeholder={placeholder}
				className="min-h-[140px]"
			/>
			<div className="flex items-center justify-between">
				<span
					className={`text-xs ${
						overLimit ? "text-destructive" : "text-muted-foreground"
					}`}
				>
					{draft.length} / {PROMPT_FIELD_MAX_CHARS}
				</span>
				{isDirty && (
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setDraft(current)}
						>
							Cancel
						</Button>
						<Button
							size="sm"
							disabled={overLimit}
							onClick={() => updateSettings({ [settingKey]: draft } as Partial<Settings>)}
						>
							Save
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

export default function Narratives() {
	useDocumentTitle("Narratives");
	const { settings, updateSettings } = useSettingsCache();

	return (
		<div>
			<h2 className="text-lg font-semibold mb-4">Narratives</h2>

			<SectionHeading>Style</SectionHeading>
			<SettingRow
				title="Paralegal mode"
				description="Adjust narrative style and formatting for paralegal billing"
			>
				<Switch
					checked={settings.paralegal_mode}
					onCheckedChange={(v) => updateSettings({ paralegal_mode: v })}
				/>
			</SettingRow>
			<SettingRow
				title="Draft detailed narratives"
				description="Generate longer, more detailed narrative descriptions for each entry"
			>
				<Switch
					checked={settings.insurance_defense_mode}
					onCheckedChange={(v) =>
						updateSettings({ insurance_defense_mode: v })
					}
				/>
			</SettingRow>
			{!settings.insurance_defense_mode && (
				<div className="pl-6">
					<SettingRow
						title="Check for restricted words"
						description="Flag narratives that contain words from your restricted words list"
					>
						<Switch
							checked={settings.check_for_red_words}
							onCheckedChange={(v) =>
								updateSettings({ check_for_red_words: v })
							}
						/>
					</SettingRow>
					<SettingRow
						title="Max word count"
						description="Maximum number of words allowed in a generated narrative"
					>
						<NumberStepper
							value={settings.word_count}
							onChange={(v) => updateSettings({ word_count: v })}
							min={5}
							step={10}
						/>
					</SettingRow>
				</div>
			)}
			<PromptBlock
				title="Narrative style"
				description="General narrative style applied to every generated description (e.g. punctuation, phrasing, formatting). This is not for changing how Group or Merge blend entries."
				placeholder="e.g. End narratives with a period. Prefer 'review' over 'analyze'."
				settingKey="p_narrative_style_prompt"
				settings={settings}
				updateSettings={updateSettings}
			/>

			<SectionHeading>Inclusion</SectionHeading>
			{settings.p_track_internal_emails && (
				<SettingRow
					title="Include internal names in narratives"
					description="When tracking internal emails, allow names of internal participants to appear in the generated narrative. When off, internal names are scrubbed."
				>
					<Switch
						checked={settings.p_include_internal_in_narratives}
						onCheckedChange={(v) =>
							updateSettings({ p_include_internal_in_narratives: v })
						}
					/>
				</SettingRow>
			)}
			<SettingRow
				title="Add page numbers to narratives"
				description="Append page or slide counts to generated narratives (e.g. '5 pages')."
			>
				<Switch
					checked={settings.add_page_numbers}
					onCheckedChange={(v) => updateSettings({ add_page_numbers: v })}
				/>
			</SettingRow>

			<SectionHeading>Group</SectionHeading>
			<SettingRow
				title="Include units in grouped entries"
				description="Append each segment's units in parentheses, e.g. (0.1), when stacking entries with Group."
			>
				<Switch
					checked={settings.p_group_include_units}
					onCheckedChange={(v) =>
						updateSettings({ p_group_include_units: v })
					}
				/>
			</SettingRow>
			<PromptBlock
				title="Group instructions"
				description="Behavioral overrides applied when stacking entries via Group. Use this for instructions like always including the time of day when stacking emails."
				placeholder="e.g. Always include the time of day when stacking emails."
				settingKey="p_group_behavior_prompt"
				settings={settings}
				updateSettings={updateSettings}
			/>
		</div>
	);
}
